import React, { useEffect, useState } from "react";
import ReactMarkdown from "react-markdown";
import FloatingTile from "../components/FloatingTile";
import BlogModal from "../components/BlogModal";
import SubscribeForm from "../components/SubscribeForm";
import SiteNav from "../components/SiteNav";
import SiteFooter from "../components/SiteFooter";
import Hero from "../components/Hero";
import { fetchFeaturedPosts, fetchPost, fetchPreviewPost } from "../lib/api";
import { useSettings } from "../context/SettingsContext";

const PLACEHOLDER_TILES = [
  { id: "ph-1", slug: "placeholder-1", title: "First post loading…", snippet: "The Index is being prepared.", tag: "Soon", thumb: "" },
  { id: "ph-2", slug: "placeholder-2", title: "Notes incoming.", snippet: "Subscribe to be the first reader.", tag: "Soon", thumb: "" },
  { id: "ph-3", slug: "placeholder-3", title: "Drafts in flight.", snippet: "Operator realism, investor lens.", tag: "Soon", thumb: "" },
  { id: "ph-4", slug: "placeholder-4", title: "On founder mindset.", snippet: "What conviction looks like with thin data.", tag: "Soon", thumb: "" },
  { id: "ph-5", slug: "placeholder-5", title: "Capital, but patient.", snippet: "Why timelines define great outcomes.", tag: "Soon", thumb: "" },
  { id: "ph-6", slug: "placeholder-6", title: "Notes on building.", snippet: "Small, ugly, repeatable systems that compound.", tag: "Soon", thumb: "" },
];

const POSTS_CACHE_KEY = "blog_featured_posts_cache_v1";

const readPostsCache = () => {
  try {
    const raw = localStorage.getItem(POSTS_CACHE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

const writePostsCache = (data) => {
  try {
    localStorage.setItem(POSTS_CACHE_KEY, JSON.stringify(data));
  } catch {
    // ignore
  }
};

export default function Home() {
  const { settings } = useSettings();
  const [modalOpen, setModalOpen] = useState(false);
  const [activePost, setActivePost] = useState(null);
  // Initialise from cache → returning visitors see real tiles instantly even
  // while the backend is waking up from a Render free-tier cold start.
  const [posts, setPosts] = useState(() => readPostsCache() || []);

  useEffect(() => {
    let cancelled = false;
    fetchFeaturedPosts()
      .then((data) => {
        if (!cancelled) {
          setPosts(data);
          writePostsCache(data);
        }
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  // Deep-link: if URL has ?post=slug OR ?preview=token, fetch and open in modal.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const params = new URLSearchParams(window.location.search);
    const slug = params.get("post");
    const previewToken = params.get("preview");
    if (!slug && !previewToken) return;
    let cancelled = false;

    const fetcher = previewToken
      ? fetchPreviewPost(previewToken)
      : fetchPost(slug);

    fetcher
      .then((p) => {
        if (!cancelled && p) {
          setActivePost(p);
          setModalOpen(true);
        }
      })
      .catch(() => {
        const next = new URLSearchParams(window.location.search);
        next.delete("post");
        next.delete("preview");
        const qs = next.toString();
        window.history.replaceState(
          {},
          "",
          qs ? `${window.location.pathname}?${qs}` : window.location.pathname,
        );
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const openIndex = () => {
    setActivePost(null);
    setModalOpen(true);
  };

  const openPost = (post) => {
    if (post?.slug?.startsWith("placeholder-")) {
      setActivePost(null);
    } else {
      setActivePost(post);
    }
    setModalOpen(true);
  };

  const tiles = (posts.length > 0 ? posts : PLACEHOLDER_TILES).slice(0, 6);

  return (
    <div
      className="relative h-screen w-screen overflow-hidden bg-white text-zinc-900"
      data-testid="home-page"
    >
      <SiteNav onOpenBlogPosts={openIndex} />
      <Hero />

      <main
        data-testid="main-section"
        className="relative w-full h-[calc(67vh-72px)] overflow-hidden"
      >
        <div className="absolute inset-0 pointer-events-none">
          {tiles.map((p, i) => (
            <div key={p.id || p.slug || i} className="pointer-events-auto">
              <FloatingTile post={p} index={i} number={i + 1} onClick={openPost} />
            </div>
          ))}
        </div>

        <section
          data-testid="about-section"
          className="relative z-10 h-full max-w-3xl mx-auto px-6 md:px-10 flex flex-col justify-center fade-up"
        >
          <span className="font-display text-[10px] md:text-xs font-bold uppercase tracking-[0.32em] text-[#002FA7] mb-3">
            ◆ {settings.about_label}
          </span>
          <h1 className="font-display font-black tracking-[-0.035em] leading-[0.92] text-4xl md:text-5xl lg:text-6xl mb-5">
            {settings.about_heading}
          </h1>
          <div className="text-zinc-600 text-[13px] md:text-sm leading-relaxed max-w-xl prose prose-sm prose-zinc max-w-xl prose-strong:text-zinc-900 prose-strong:font-semibold prose-p:my-2">
            <ReactMarkdown>{settings.about_body}</ReactMarkdown>
          </div>

          <div
            data-testid="subscribe-block"
            className="mt-6 flex flex-col sm:flex-row sm:items-center gap-3 max-w-xl"
          >
            <span className="font-display text-[10px] font-bold uppercase tracking-[0.22em] text-zinc-900 whitespace-nowrap">
              {settings.subscribe_label}
            </span>
            <SubscribeForm />
          </div>
        </section>
      </main>

      <SiteFooter />

      <BlogModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        initialPost={activePost}
      />
    </div>
  );
}
